import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Send, Star } from "lucide-react";

const Footer = () => {
  // Social links provided by user
  const socialLinks = {
    whatsapp: "https://wa.me/18554712511",
    teams: "https://teams.live.com/l/invite/FEAF6e2oWJeNwALLTk?v=g1",
    telegram: "https://t.me/DigitalMasterMedia"
  };

  const servicesLinks = [
    { label: "Website Development", path: "/website-development" },
    { label: "CRM Development", path: "/services/crm-development" },
    { label: "Custom Software", path: "/services/custom-software" },
    { label: "AI Software", path: "/services/ai-software" },
    { label: "Business Automation", path: "/services/business-automation" },
  ];

  const quickLinks = [
    { label: "Home", path: "/" },
    { label: "About Us", path: "/about" },
    { label: "PPC Offers", path: "/offers" },
    { label: "PPC Courses", path: "/learn" },
    { label: "CallIntell AI", path: "/callintell" },
    { label: "FAQ", path: "/faq" },
    { label: "Contact", path: "/contact" },
  ];

  const partnerLinks = [
    { label: "We Are Hiring!", path: "/careers" },
    { label: "Privacy Policy", path: "/privacy" },
    { label: "Terms of Service", path: "/terms" },
  ];

  const ColumnHeading = ({ children, testid }) => (
    <div className="mb-5">
      <h4 className="font-bold text-sm uppercase tracking-wider text-white" data-testid={testid}>{children}</h4>
      <div className="w-10 h-0.5 bg-blue-500 mt-2.5 rounded-full"></div>
    </div>
  );

  const dot = <span className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0"></span>;
  const linkCls = "text-slate-400 text-sm leading-6 hover:text-blue-400 transition-colors flex items-center gap-2";

  return (
    <footer className="bg-slate-900 text-slate-300" data-testid="footer">
      {/* Trust Badge Section */}
      <div className="border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-center">
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className={`w-6 h-6 ${i < 4 ? 'text-yellow-400 fill-yellow-400' : 'text-yellow-400 fill-yellow-400/50'}`} />
              ))}
            </div>
            <div>
              <p className="text-xl font-bold text-white">4.5 Star Trusted Network Rating</p>
              <p className="text-slate-400">Trusted by publishers and advertisers across the United States</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer — 5 equal columns, stacked on mobile */}
      <div className="max-w-7xl mx-auto px-4 py-10 sm:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-x-10 gap-y-12 items-start">

          {/* Column 1 — Company */}
          <div className="text-left" data-testid="footer-col-company">
            <ColumnHeading testid="footer-heading-company">Company</ColumnHeading>
            <img
              src="/logo_no_bg.png"
              alt="Digital Master Media LLC"
              className="h-12 w-auto mb-4 object-contain"
            />
            <ul className="space-y-2.5 mb-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-blue-400 flex-shrink-0 mt-1" />
                <span className="text-slate-400 text-sm leading-6">30 N Gould St, Ste R<br />Sheridan, WY 82801, USA</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-blue-400 flex-shrink-0" />
                <a href="tel:+18554712511" className="text-slate-400 text-sm leading-6 whitespace-nowrap hover:text-blue-400 transition-colors">+1 (855) 471-2511</a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-blue-400 flex-shrink-0" />
                <a href="mailto:abidali@digitalmastermedia.com" className="text-slate-400 text-[13px] leading-6 whitespace-nowrap hover:text-blue-400 transition-colors" data-testid="footer-email-link">abidali@digitalmastermedia.com</a>
              </li>
            </ul>
            {/* Social Links */}
            <div className="flex items-center gap-4" data-testid="footer-social-row">
              <a
                href={socialLinks.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Chat on WhatsApp"
                title="WhatsApp: +1 (855) 471-2511"
                data-testid="social-whatsapp"
                className="w-10 h-10 rounded-full bg-[#25D366] flex items-center justify-center hover:bg-[#1EBE5A] transition-colors text-white shadow-md hover:shadow-lg"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M20.52 3.48A11.9 11.9 0 0012.06 0C5.5 0 .17 5.34.17 11.9c0 2.1.55 4.15 1.6 5.96L0 24l6.32-1.65a11.9 11.9 0 005.73 1.46h.01c6.56 0 11.89-5.34 11.89-11.9 0-3.18-1.24-6.16-3.43-8.43zM12.06 21.4h-.01a9.5 9.5 0 01-4.84-1.33l-.35-.21-3.75.98 1-3.66-.23-.38a9.48 9.48 0 01-1.46-5.05c0-5.24 4.27-9.5 9.53-9.5 2.54 0 4.93.99 6.73 2.79a9.44 9.44 0 012.79 6.72c0 5.24-4.27 9.5-9.53 9.5zm5.51-7.11c-.3-.15-1.78-.88-2.06-.98-.28-.1-.48-.15-.68.15-.2.3-.78.98-.96 1.18-.18.2-.36.22-.66.07-.3-.15-1.27-.47-2.42-1.5-.9-.8-1.5-1.79-1.68-2.09-.18-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.18.2-.3.3-.5.1-.2.05-.38-.02-.53-.07-.15-.68-1.63-.93-2.24-.24-.58-.49-.5-.68-.51l-.58-.01c-.2 0-.53.07-.8.38-.28.3-1.06 1.04-1.06 2.53s1.08 2.94 1.23 3.14c.15.2 2.12 3.24 5.14 4.55.72.31 1.28.5 1.72.63.72.23 1.37.2 1.89.12.58-.09 1.78-.73 2.03-1.43.25-.7.25-1.3.18-1.43-.07-.13-.28-.2-.58-.35z"/>
                </svg>
              </a>
              <a
                href={socialLinks.telegram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Chat on Telegram"
                data-testid="social-telegram"
                className="w-10 h-10 rounded-full bg-[#0088cc] flex items-center justify-center hover:bg-[#006699] transition-colors text-white shadow-md hover:shadow-lg"
              >
                <Send className="w-5 h-5" />
              </a>
              <a
                href={socialLinks.teams}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Chat on Microsoft Teams"
                data-testid="social-teams"
                className="w-10 h-10 rounded-full bg-[#6264A7] flex items-center justify-center hover:bg-[#464775] transition-colors text-white shadow-md hover:shadow-lg"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.625 8.5h-3.75V6.25a1.875 1.875 0 00-1.875-1.875h-6a1.875 1.875 0 00-1.875 1.875V8.5H3.375A1.875 1.875 0 001.5 10.375v7.5a1.875 1.875 0 001.875 1.875h17.25a1.875 1.875 0 001.875-1.875v-7.5A1.875 1.875 0 0020.625 8.5zM9 6.25a.125.125 0 01.125-.125h5.75a.125.125 0 01.125.125V8.5H9V6.25z"/>
                </svg>
              </a>
              <a
                href="https://www.facebook.com/profile.php?id=61580378221826"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-facebook"
                className="w-10 h-10 rounded-full bg-[#1877F2] flex items-center justify-center hover:bg-[#166FE5] transition-colors text-white shadow-md hover:shadow-lg"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Column 2 — Quick Links */}
          <div data-testid="footer-col-quick-links">
            <ColumnHeading testid="footer-heading-quick-links">Pay Per Call Network</ColumnHeading>
            <ul className="space-y-2.5">
              {quickLinks.map((item) => (
                <li key={item.label}>
                  {item.href ? (
                    <a href={item.href} target="_blank" rel="noopener noreferrer" className={linkCls}>{dot}{item.label}</a>
                  ) : (
                    <Link to={item.path} className={linkCls}>{dot}{item.label}</Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3 — Services (10 items, slightly tighter) */}
          <div data-testid="footer-col-services">
            <ColumnHeading testid="footer-heading-services">AI Developers</ColumnHeading>
            <ul className="space-y-2">
              {servicesLinks.map((item) => (
                <li key={item.label}>
                  <Link to={item.path} className="text-slate-400 text-[13px] leading-6 hover:text-blue-400 transition-colors flex items-center gap-2" data-testid={`footer-service-link-${item.label.toLowerCase().replace(/[\s/]+/g, "-")}`}>
                    {dot}
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4 — For Partners */}
          <div data-testid="footer-col-partners">
            <ColumnHeading testid="footer-heading-partners">For Partners</ColumnHeading>
            <ul className="space-y-2.5">
              {partnerLinks.map((item) => (
                <li key={item.label}>
                  <Link to={item.path} className={linkCls}>
                    <span className="w-1.5 h-1.5 rounded-full bg-purple-500 flex-shrink-0"></span>
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 5 — Join Our Network */}
          <div data-testid="footer-col-join">
            <ColumnHeading testid="footer-heading-join">Join Our Network</ColumnHeading>
            <div className="space-y-3">
              <Link
                to="/apply"
                className="block w-full py-2.5 px-5 bg-blue-600 text-white text-center text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
              >
                Publisher Sign Up
              </Link>
              <Link
                to="/buyer"
                className="block w-full py-2.5 px-5 border-2 border-slate-600 text-slate-300 text-center text-sm font-semibold rounded-lg hover:border-blue-400 hover:text-white transition-colors"
              >
                Buyer Sign Up
              </Link>
            </div>
            <p className="text-slate-500 text-sm leading-6 mt-4">
              Join hundreds of publishers and advertisers growing their business with us.
            </p>
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="border-t border-slate-700">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <p className="text-slate-500 text-sm text-center leading-relaxed">
            Digital Master Media LLC is a premier Pay-Per-Call and Lead Generation Network dedicated to bridging the gap between publishers, affiliates, and local service providers across the United States. Through high-performing, result-driven performance marketing campaigns, we empower businesses to scale effectively while helping affiliates monetize their traffic efficiently.
          </p>
        </div>
      </div>

      {/* TCPA Compliance */}
      <div className="border-t border-slate-700 bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-center gap-2 mb-3">
            <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            <h4 className="text-slate-300 font-semibold text-sm">TCPA Compliance</h4>
          </div>
          <p className="text-slate-500 text-xs text-center leading-relaxed max-w-4xl mx-auto">
            Digital Master Media LLC is fully compliant with the Telephone Consumer Protection Act (TCPA). All calls generated through our network are made in accordance with federal regulations. We require all publishers to obtain proper consent before initiating calls, maintain Do-Not-Call list compliance, and adhere to calling time restrictions. Our network monitors call quality and compliance to protect both consumers and advertisers.
          </p>
        </div>
      </div>

      {/* Bottom Bar — own row, centered, separated by thin top border */}
      <div className="border-t border-slate-700">
        <div className="max-w-7xl mx-auto px-4 py-6 flex flex-col items-center justify-center gap-3 text-center">
          <p className="text-slate-500 text-sm" data-testid="footer-copyright">
            © 2021 Digital Master Media LLC. All rights reserved.
          </p>
          <div className="flex items-center gap-6 text-sm">
            <Link to="/privacy" className="text-slate-500 hover:text-blue-400 transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-slate-500 hover:text-blue-400 transition-colors">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
